// HFL / MBL dosyalarının aşama, evrak, öncelik ve finans özetini sağdan açılan panelde gösterir.
// Ocean, Air ve Road dashboard'ları ortak kullanır; hangi bölümlerin görüneceğini `status` nesnesi belirler.
import React from "react";
import Drawer from "@mui/material/Drawer";
import { X, ExternalLink, CheckCircle2, XCircle, Circle, AlertTriangle, Info, Timer } from "lucide-react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { UI, TONES, toneColor, priorityTone, formatMoney, StatusPill } from "./oceanDashboardUi";

function Section({ title, right, children }) {
    return (
        <MDBox px={3} py={2.25} sx={{ borderBottom: `1px solid ${UI.border}` }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <MDTypography
                    sx={{ fontSize: 11.5, fontWeight: 700, color: UI.muted, letterSpacing: 0.6, textTransform: "uppercase" }}
                >
                    {title}
                </MDTypography>
                {right}
            </MDBox>
            {children}
        </MDBox>
    );
}

function Milestones({ steps }) {
    return (
        <MDBox>
            {steps.map((step, index) => {
                const last = index === steps.length - 1;
                const color = step.done ? UI.green : step.current ? UI.blue : UI.subtle;
                const Glyph = step.done ? CheckCircle2 : Circle;

                return (
                    <MDBox key={step.label} display="flex" gap={1.5}>
                        <MDBox display="flex" flexDirection="column" alignItems="center">
                            <MDBox
                                sx={{
                                    width: 22,
                                    height: 22,
                                    display: "grid",
                                    placeItems: "center",
                                    borderRadius: "50%",
                                    backgroundColor: step.current ? TONES.info.bg : "transparent",
                                }}
                            >
                                <Glyph size={step.current ? 14 : 18} color={color} fill={step.current ? UI.blue : "none"} />
                            </MDBox>
                            {!last && (
                                <MDBox
                                    sx={{
                                        width: 2,
                                        flex: 1,
                                        minHeight: 18,
                                        my: 0.25,
                                        backgroundColor: step.done ? UI.green : UI.border,
                                    }}
                                />
                            )}
                        </MDBox>
                        <MDBox pb={last ? 0 : 1.5} flex={1} display="flex" justifyContent="space-between" gap={1}>
                            <MDBox>
                                <MDTypography
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: step.current ? 700 : 500,
                                        color: step.done || step.current ? UI.text : UI.muted,
                                        lineHeight: "22px",
                                    }}
                                >
                                    {step.label}
                                </MDTypography>
                                {step.current && (
                                    <MDTypography sx={{ fontSize: 11.5, color: UI.blue }}>Current stage</MDTypography>
                                )}
                            </MDBox>
                            {step.date && (
                                <MDTypography sx={{ fontSize: 12, color: UI.muted, lineHeight: "22px", whiteSpace: "nowrap" }}>
                                    {step.date}
                                </MDTypography>
                            )}
                        </MDBox>
                    </MDBox>
                );
            })}
        </MDBox>
    );
}

// Evrak / acente gibi var-yok listesi; `progress` verilirse üstte tamamlanma çubuğu çizilir
function Checklist({ list }) {
    const missing = list.items.filter((i) => !i.ok).length;
    const pct = list.items.length ? Math.round(((list.items.length - missing) / list.items.length) * 100) : 0;

    return (
        <Section
            title={list.title}
            right={
                <MDTypography sx={{ fontSize: 12, fontWeight: 700, color: missing ? UI.red : UI.green }}>
                    {list.progress ? `${pct}% · ` : ""}
                    {missing ? `${missing} missing` : "complete"}
                </MDTypography>
            }
        >
            {list.progress && (
                <MDBox sx={{ height: 6, borderRadius: 3, backgroundColor: "#EEF2F6", overflow: "hidden", mb: 1.5 }}>
                    <MDBox
                        sx={{
                            height: "100%",
                            width: `${pct}%`,
                            backgroundColor: pct === 100 ? UI.green : pct >= 40 ? UI.orange : UI.red,
                        }}
                    />
                </MDBox>
            )}
            {list.items.map((item) => (
                <MDBox
                    key={item.name}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1.5}
                    py={0.9}
                    sx={{ borderBottom: `1px solid ${UI.border}`, "&:last-of-type": { borderBottom: "none" } }}
                >
                    <MDBox display="flex" alignItems="center" gap={1.25} flexShrink={0}>
                        {item.ok ? <CheckCircle2 size={16} color={UI.green} /> : <XCircle size={16} color={UI.red} />}
                        <MDTypography sx={{ fontSize: 13, color: UI.text }}>{item.name}</MDTypography>
                    </MDBox>
                    <MDTypography
                        title={item.value}
                        sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: item.ok ? (item.value ? UI.text : UI.green) : UI.red,
                            textAlign: "right",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {item.ok ? item.value || "Uploaded" : "Missing"}
                    </MDTypography>
                </MDBox>
            ))}
        </Section>
    );
}

function FreeTimeBox({ label, daysLeft }) {
    const tone = daysLeft === null ? "neutral" : daysLeft <= 0 ? "danger" : daysLeft <= 2 ? "warning" : "success";
    const t = TONES[tone];

    return (
        <MDBox flex={1} p={1.5} sx={{ borderRadius: 1.5, backgroundColor: t.bg }}>
            <MDTypography sx={{ fontSize: 11.5, color: UI.muted }}>{label}</MDTypography>
            <MDTypography sx={{ fontSize: 16, fontWeight: 700, color: t.fg }}>
                {daysLeft === null ? "Not set" : daysLeft <= 0 ? "Expired" : `${daysLeft} days left`}
            </MDTypography>
        </MDBox>
    );
}

const linkSx = {
    color: UI.blue,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
    "&:hover": { textDecoration: "underline" },
};

function FileStatusDrawer({ status, currency, onClose }) {
    const open = Boolean(status);
    const s = status || {};
    const accent = toneColor(priorityTone(s.priority));
    const isIssue = s.priority && s.priority !== "Normal";
    const reasonTone = TONES[isIssue ? priorityTone(s.priority) : "success"];

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: "100%", sm: 440 },
                    m: 0,
                    height: "100%",
                    borderRadius: 0,
                    backgroundColor: "#fff",
                    boxShadow: "-12px 0 32px rgba(15,23,42,0.12)",
                },
            }}
        >
            {open && (
                <MDBox display="flex" flexDirection="column" height="100%">
                    {/* Başlık */}
                    <MDBox px={3} pt={2.5} pb={2} sx={{ borderBottom: `1px solid ${UI.border}`, borderTop: `4px solid ${accent}` }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                            <MDBox minWidth={0}>
                                <MDTypography sx={{ fontSize: 20, fontWeight: 700, color: UI.text }}>{s.title}</MDTypography>
                                <MDTypography sx={{ fontSize: 13, color: UI.muted }}>{s.subtitle}</MDTypography>
                            </MDBox>
                            <MDBox
                                component="button"
                                type="button"
                                onClick={onClose}
                                aria-label="Close"
                                sx={{
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    p: 0.75,
                                    borderRadius: 1,
                                    display: "grid",
                                    "&:hover": { backgroundColor: "#F1F5F9" },
                                }}
                            >
                                <X size={18} color={UI.muted} />
                            </MDBox>
                        </MDBox>

                        <MDBox display="flex" gap={1} mt={1.5} flexWrap="wrap">
                            {(s.pills || []).map((pill) => (
                                <StatusPill key={pill.label} label={pill.label} tone={pill.tone} />
                            ))}
                        </MDBox>

                        {s.facts?.length > 0 && (
                            <MDBox mt={2} display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                                {s.facts.map(([k, v]) => (
                                    <MDBox key={k} minWidth={0}>
                                        <MDTypography sx={{ fontSize: 11.5, color: UI.muted }}>{k}</MDTypography>
                                        <MDTypography
                                            title={v}
                                            sx={{ fontSize: 13, fontWeight: 600, color: UI.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                        >
                                            {v || "—"}
                                        </MDTypography>
                                    </MDBox>
                                ))}
                            </MDBox>
                        )}
                    </MDBox>

                    <MDBox flex={1} sx={{ overflowY: "auto" }}>
                        {s.reasons?.length > 0 && (
                            <Section title="Why this priority">
                                <MDBox display="flex" flexDirection="column" gap={1}>
                                    {s.reasons.map((reason) => {
                                        const Glyph = isIssue ? AlertTriangle : Info;
                                        return (
                                            <MDBox
                                                key={reason}
                                                display="flex"
                                                alignItems="center"
                                                gap={1.25}
                                                px={1.5}
                                                py={1}
                                                sx={{ borderRadius: 1.5, backgroundColor: reasonTone.bg }}
                                            >
                                                <Glyph size={16} color={reasonTone.fg} />
                                                <MDTypography sx={{ fontSize: 13, color: reasonTone.fg, fontWeight: 600 }}>
                                                    {reason}
                                                </MDTypography>
                                            </MDBox>
                                        );
                                    })}
                                </MDBox>
                            </Section>
                        )}

                        {s.milestones?.length > 0 && (
                            <Section title={s.milestonesTitle || "Shipment stage"}>
                                <Milestones steps={s.milestones} />
                            </Section>
                        )}

                        {(s.checklists || []).map((list) => (
                            <Checklist key={list.title} list={list} />
                        ))}

                        {s.links && (
                            <Section
                                title={s.links.title}
                                right={<MDTypography sx={{ fontSize: 12, fontWeight: 700, color: UI.muted }}>{s.links.items.length}</MDTypography>}
                            >
                                {s.links.items.length ? (
                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                        {s.links.items.map((link, index) => (
                                            <MDBox
                                                key={`${link.label}-${index}`}
                                                component="span"
                                                onClick={link.onClick}
                                                sx={{ ...linkSx, px: 1, py: 0.4, borderRadius: 1, backgroundColor: TONES.info.bg }}
                                            >
                                                {link.label}
                                            </MDBox>
                                        ))}
                                    </MDBox>
                                ) : (
                                    <MDTypography sx={{ fontSize: 13, color: UI.orange, fontWeight: 600 }}>{s.links.empty}</MDTypography>
                                )}
                            </Section>
                        )}

                        {s.freeTime && (
                            <Section title="Free time" right={<Timer size={14} color={UI.muted} />}>
                                <MDBox display="flex" gap={1.5}>
                                    <FreeTimeBox label="Demurrage" daysLeft={s.freeTime.demurrage} />
                                    <FreeTimeBox label="Detention" daysLeft={s.freeTime.detention} />
                                </MDBox>
                            </Section>
                        )}

                        {s.financials && (
                            <Section title={`Financials · ${currency}`}>
                                <MDBox display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={1.5}>
                                    {[
                                        ["Selling", s.financials.selling, UI.text],
                                        [s.financials.buyingLabel || "Buying", s.financials.buying, UI.text],
                                        [
                                            "Profit",
                                            s.financials.profit,
                                            s.financials.profit > 0 ? UI.green : s.financials.profit < 0 ? UI.red : UI.muted,
                                        ],
                                    ].map(([k, v, c]) => (
                                        <MDBox key={k}>
                                            <MDTypography sx={{ fontSize: 11.5, color: UI.muted }}>{k}</MDTypography>
                                            <MDTypography sx={{ fontSize: 14, fontWeight: 700, color: c }}>
                                                {formatMoney(v, currency)}
                                            </MDTypography>
                                        </MDBox>
                                    ))}
                                </MDBox>
                                {s.financials.selling ? (
                                    <MDTypography sx={{ fontSize: 12, color: UI.muted, mt: 1 }}>
                                        Margin {((s.financials.profit / s.financials.selling) * 100).toFixed(1)}%
                                    </MDTypography>
                                ) : null}
                            </Section>
                        )}
                    </MDBox>

                    {s.onOpen && (
                        <MDBox px={3} py={2} sx={{ borderTop: `1px solid ${UI.border}` }}>
                            <MDBox
                                component="button"
                                type="button"
                                onClick={s.onOpen}
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    py: 1.25,
                                    border: "none",
                                    borderRadius: 1.5,
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    fontSize: 13.5,
                                    fontWeight: 600,
                                    color: "#fff",
                                    backgroundColor: UI.navy,
                                    "&:hover": { backgroundColor: "#13305A" },
                                }}
                            >
                                {s.openLabel || "Open file"}
                                <ExternalLink size={15} />
                            </MDBox>
                        </MDBox>
                    )}
                </MDBox>
            )}
        </Drawer>
    );
}

export default FileStatusDrawer;
